import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static classes = [
    'progress',
    'progressLoser',
    'progressWinner',
    'revealed',
    'timerLoser',
    'timerWinner'
  ]
  static targets = ['count', 'progress', 'racer', 'replay', 'result', 'timer']
  static values = {
    delay: { type: Number, default: 1 },
    duration: { type: Number, default: 12 },
    emails: Number,
    units: Object
  }

  connect() {
    this.formatter = new Intl.NumberFormat(document.documentElement.lang)
    this.reducedMotionQuery = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    )

    this.lanes = this.racerTargets.map((racer, index) => ({
      count: this.countTargets[index],
      progress: this.progressTargets[index],
      result: this.resultTargets[index],
      seconds: Number(racer.dataset.seconds),
      timer: this.timerTargets[index]
    }))

    this.fastest = Math.min(...this.lanes.map(lane => lane.seconds))
    this.slowest = Math.max(...this.lanes.map(lane => lane.seconds))

    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return

        this.observer.disconnect()
        this.#start(this.delayValue)
      },
      { threshold: 0.35 }
    )

    this.observer.observe(this.element)
  }

  disconnect() {
    this.observer.disconnect()
    clearTimeout(this.timeout)
    cancelAnimationFrame(this.frame)
  }

  replay() {
    if (!this.done) return

    this.#start()
  }

  #start(delay = 0) {
    clearTimeout(this.timeout)
    cancelAnimationFrame(this.frame)

    this.done = false
    this.replayTarget.classList.remove(...this.revealedClasses)

    this.lanes.forEach(lane => {
      lane.finished = false
      lane.result.classList.remove(...this.revealedClasses)
      lane.progress.classList.remove(
        ...this.progressWinnerClasses,
        ...this.progressLoserClasses
      )
      lane.progress.classList.add(...this.progressClasses)
      lane.timer.classList.remove(
        ...this.timerWinnerClasses,
        ...this.timerLoserClasses
      )
    })

    if (this.reducedMotionQuery.matches) {
      this.#renderLanes(this.slowest)
      this.#finish()

      return
    }

    // Both lanes sit at zero for a beat so the section reads as a starting
    // line before anything moves.
    this.#renderLanes(0)

    this.timeout = setTimeout(() => {
      this.startedAt = performance.now()
      this.frame = requestAnimationFrame(now => this.#tick(now))
    }, delay * 1000)
  }

  #tick(now) {
    const elapsed = (now - this.startedAt) / 1000
    const progress = Math.min(elapsed / this.durationValue, 1)

    // Simulated time ramps up rather than running linearly so the whole race
    // fits in a few seconds without the faster lane finishing instantly.
    this.#renderLanes(this.slowest * progress ** 2)

    if (progress < 1) {
      this.frame = requestAnimationFrame(nextNow => this.#tick(nextNow))
    } else {
      this.#finish()
    }
  }

  // Both badges wait for the slower lane
  #finish() {
    this.done = true
    this.lanes.forEach(lane => {
      lane.result.classList.add(...this.revealedClasses)
    })
    this.replayTarget.classList.add(...this.revealedClasses)
  }

  #renderLanes(simulated) {
    this.lanes.forEach(lane => {
      const ratio = Math.min(simulated / lane.seconds, 1)

      lane.progress.style.strokeDashoffset = 100 - ratio * 100
      lane.count.textContent = this.formatter.format(
        Math.floor(ratio * this.emailsValue)
      )
      lane.timer.textContent = this.#clock(Math.min(simulated, lane.seconds))

      if (ratio === 1 && !lane.finished) {
        const won = lane.seconds === this.fastest

        lane.finished = true
        lane.progress.classList.remove(...this.progressClasses)
        lane.progress.classList.add(
          ...(won ? this.progressWinnerClasses : this.progressLoserClasses)
        )
        lane.timer.classList.add(
          ...(won ? this.timerWinnerClasses : this.timerLoserClasses)
        )
      }
    })
  }

  #clock(seconds) {
    const total = Math.floor(seconds)
    const hours = Math.floor(total / 3600)
    const minutes = Math.floor((total % 3600) / 60)
    const units = this.unitsValue
    const pad = value => String(value).padStart(2, '0')
    // The leading unit keeps its tens column as a monospace-width space, so
    // the digit only shows up once it is needed and the timer never resizes.
    const lead = value => String(value).padStart(2, '\u00a0')

    if (hours > 0) {
      return `${lead(hours)}${units.hours} ${pad(minutes)}${units.minutes}`
    }

    return `${lead(minutes)}${units.minutes} ${pad(total % 60)}${units.seconds}`
  }
}
